import { trackByIndex } from 'src/app/shared/utils/track-by.util';
import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { IntegrationType } from 'src/app/enums/IntegrationType.enum';
import { GridViewType } from 'src/app/enums/gridViewType.enum';
import { LabIntegrationDialogComponent } from 'src/app/modals/lab-integration-dialog/lab-integration-dialog.component';
import { Lab, TAT } from 'src/app/models/lab.model';
import { Sample } from 'src/app/models/sample.model';
import { IhLabResult } from 'src/app/models/IhLabResult.model';
import { GridView } from 'src/app/models/gridView.model';
import { GridColumnSettings } from 'src/app/models/gridColumnSettings.model';
import { RolePermissionObjects } from 'src/app/shared/constants/rolePermissionObjects.constants';
import { LabService } from 'src/app/services/lab.service';
import { GridViewService } from 'src/app/services/grid-view.service';
import { SampleService } from 'src/app/services/sample.service';
import { ToastService } from 'src/app/services/toast.service';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { getSampleGridIdForLab } from 'src/app/shared/constants/gridIds.constants';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { GridPermissions } from 'src/app/shared/grid/core-grid/gridPermissions.enum';
import { TranslateService } from 'src/app/services/translate.service';
import { synthesizeDefaultView } from 'src/app/shared/grid/grid-view-default.helper';

export interface KeyValue { label: string; value: string | number; isCopyable?: boolean; }

@Component({
  standalone: false,
  selector: 'app-lab-integration-details',
  templateUrl: './lab-integration-details.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './lab-integration-details.component.scss'
})
export class LabIntegrationDetailsComponent implements OnInit {
  reportFields: KeyValue[];
  readonly permissionObject = RolePermissionObjects.LabRequisition;
  readonly editAction = GridPermissions.Update;
  invoiceFields: KeyValue[];
  labFields: KeyValue[];
  settingsFields: KeyValue[];
  labData: Lab | null = null;
  integrationTypeEnum = IntegrationType;
  tatColumns = [
    nameOf<TAT>(x => x.turnAroundTime),
    nameOf<TAT>(x => x.surcharge),
  ]

  sampleColumns: GridColumnSettings[] = [];
  labGridView: GridView | null = null;
  isSavingGridView: boolean = false;
  isLoadingGridConfig: boolean = false;

  private readonly defaultSampleColumns: GridColumnSettings[] = [
    { property: nameOf<Sample>(x => x.sampleNumber), displayName: 'Sample Number', visible: true, order: 0 },
    { property: nameOf<IhLabResult>(x => x.masterAgentName), displayName: 'Agent Name', visible: true, order: 1 },
    { property: nameOf<IhLabResult>(x => x.agentGroupId), displayName: 'Agent Group', visible: true, order: 2 },
    { property: nameOf<Sample>(x => x.startDate), displayName: 'Sampling Date', visible: true, order: 3 },
    { property: nameOf<Sample>(x => x.sampleVolume), displayName: 'Sample Volume', visible: true, order: 4 },
    { property: nameOf<Sample>(x => x.runTime), displayName: 'Total Time (Minutes)', visible: true, order: 5 },
    { property: nameOf<Sample>(x => x.flowRate), displayName: 'Flowrate', visible: true, order: 6 },
    { property: nameOf<Sample>(x => x.analyticalMethodName), displayName: 'Method Reference', visible: true, order: 7 },
    { property: nameOf<Sample>(x => x.alternateSampleNumber), displayName: 'Alternate Sample Number', visible: false, order: 8 },
  ];

  constructor(private labService: LabService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private toastService: ToastService,
    private gridViewService: GridViewService,
    private sampleService: SampleService,
    protected dialog: MatDialog,
    private translateService: TranslateService
  ) { }

  ngOnInit(): void {
    const id = this.activatedRoute.snapshot.params.id;
    if (!id) {
      this.router.navigate(['/dashboard/integrations/lab-integrations']);
      return;
    }
    this.getLabDetails(id);
  }

  getLabDetails(id: string) {
    this.labService.getById(id).subscribe({
      next: (data) => {
        this.labData = data;
        this.setSectionsData(data);
        this.loadGridViewConfig(id);
      },
      error: (error) => {
        this.toastService.showError(error);
      }
    });
  }

  loadGridViewConfig(labId: string) {
    this.isLoadingGridConfig = true;
    const gridId = getSampleGridIdForLab(labId);

    forkJoin({
      gridView: this.gridViewService.getDefaultView(gridId).pipe(
        catchError(() => of(null))
      ),
      fieldConfig: this.sampleService.getFieldConfiguration().pipe(
        catchError(() => of(null))
      )
    }).subscribe({
      next: ({ gridView, fieldConfig }) => {
        if (gridView?.settingsJson?.columns) {
          this.labGridView = gridView;
          this.sampleColumns = this.defaultSampleColumns.map(col => ({ ...col }));
          if (fieldConfig) {
            this.sampleColumns.forEach(col => {
              if (fieldConfig[col.property]?.displayName) {
                col.displayName = fieldConfig[col.property].displayName;
              }
            });
          }
          const viewColumns = gridView.settingsJson.columns;
          this.sampleColumns.forEach(col => {
            const viewCol = viewColumns.find(vc => vc.property === col.property);
            if (viewCol) {
              col.visible = viewCol.visible;
              col.order = viewCol.order;
            }
          });
          this.sampleColumns.sort((a, b) => a.order - b.order);
        } else {
          const labName = this.labData?.labName ?? 'Lab';
          this.labGridView = synthesizeDefaultView(null, RolePermissionObjects.Sample, {
            viewName: `${labName} Sample Grid`,
            fallbackColumns: this.defaultSampleColumns,
          });
          this.sampleColumns = this.defaultSampleColumns.map(col => {
            const displayName = fieldConfig?.[col.property]?.displayName;
            return displayName ? { ...col, displayName } : { ...col };
          });
        }
        this.isLoadingGridConfig = false;
      },
      error: () => {
        this.labGridView = null;
        this.sampleColumns = this.defaultSampleColumns.map(col => ({ ...col }));
        this.isLoadingGridConfig = false;
      }
    });
  }

  toggleColumnVisibility(column: GridColumnSettings): void {
    column.visible = !column.visible;
  }

  saveGridViewConfig(): void {
    if (!this.labData) return;
    if (!this.sampleColumns.some(col => col.visible)) {
      this.toastService.displayErrorMessage('Please select at least one sample column', true);
      return;
    }

    this.isSavingGridView = true;

    const columns: GridColumnSettings[] = this.sampleColumns.map((col, index) => ({
      property: col.property,
      visible: col.visible,
      order: index
    }));

    const viewPayload: GridView = {
      id: this.labGridView?.id,
      gridId: getSampleGridIdForLab(this.labData.id),
      objectId: RolePermissionObjects.Sample,
      type: GridViewType.Grid,
      viewName: `${this.labData.labName} Sample Grid`,
      isShared: true,
      isMobileView: false,
      organizationLevelId: this.labData.organizationLevelId,
      settingsJson: { columns, filters: {} }
    };

    const request$ = this.labGridView?.id
      ? this.gridViewService.update(viewPayload)
      : this.gridViewService.create(viewPayload);

    request$.subscribe({
      next: (result) => {
        if (result) this.labGridView = result as GridView;
        this.toastService.showSuccess('Sample grid configuration saved', true);
        this.isSavingGridView = false;
      },
      error: (error) => {
        this.toastService.displayErrorMessage('Failed to save sample grid configuration', true);
        this.isSavingGridView = false;
      }
    });
  }

  setSectionsData(data: Lab) {
    this.reportFields = [
      { label: 'Report To', value: data.reportTo },
      { label: 'Company Name', value: data.reportToCompanyName },
      { label: 'Address 1', value: data.reportToAddress1 },
      { label: 'Address 2', value: data.reportToAddress2 },
      { label: 'City', value: data.reportToCity },
      { label: 'State', value: data.reportToState },
      { label: 'Zip', value: data.reportToZip },
      { label: 'Phone Number', value: data.reportToPhoneNumber },
      { label: 'Cell No.', value: data.reportToCellNumber },
      { label: 'Email', value: data.reportToEmail },
    ];
    this.invoiceFields = [
      { label: 'Invoice To', value: data.invoiceTo },
      { label: 'Company Name', value: data.invoiceCompanyName },
      { label: 'Address 1', value: data.invoiceAddress1 },
      { label: 'Address 2', value: data.invoiceAddress2 },
      { label: 'Phone Number', value: data.invoicePhoneNumber },
      { label: 'PO No.', value: data.invoicePoNumber },
      { label: 'Email', value: data.invoiceEmail },
      { label: 'Payment Info', value: data.invoicePaymentInfo },
    ];
    this.labFields = [
      {
        label: data.labIntegrationType === IntegrationType.AlsLab
          ? 'ALS Quote Number'
          : 'Lab Account Number',
        value: data.labAccountNumber
      },
      { label: 'Address 1', value: data.labAddress1 },
      { label: 'Address 2', value: data.labAddress2 },
      { label: 'City', value: data.labCity },
      { label: 'State', value: data.labState },
      { label: 'Zip', value: data.labZip },
    ];
    this.settingsFields = [
      { label: 'Apply Blank Correction', value: this.translateService.getDynamicTranslation(data.applyBlankCorrection ? 'Yes' : 'No') },
      { label: 'Enforce Blank Rules', value: this.translateService.getDynamicTranslation(data.enforceBlankRules ? 'Yes' : 'No') },
    ];
    if (data.enforceBlankRules) {
      this.settingsFields.push(
        { label: 'Minimum Blank Count', value: `${data.minBlankCount ?? ''}` },
        { label: 'Minimum Blank Ratio (%)', value: `${data.minBlankRatioPercent ?? ''}` },
      );
    }
    if (data.labIntegrationType === IntegrationType.GalsonLab && data.emailEdd) {
      this.settingsFields.push({
        label: 'Auto-generated Email',
        value: data.emailEdd,
        isCopyable: true,
      });
    }
  }
  openLabIntegrationDialog() {
    const dialogRef = this.dialog.open(LabIntegrationDialogComponent, {
      data: { id: this.labData.id },
      width: '760px',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe((res) => {
      this.getLabDetails(this.labData.id);
    })
  }

    public trackByIndex = trackByIndex;

  copyToClipboard(text: string | number): void {
    navigator.clipboard.writeText(String(text)).then(() => {
      this.toastService.showSuccess('Copied to clipboard', true);
    }).catch(() => {
      this.toastService.displayErrorMessage('Failed to copy', true);
    });
  }
}
